import { linkedInPage } from '../ai/enrichmentSeam';
import { PEOPLE_PATH } from '../identity/textThread';
import type { RecordCheckField } from './recordSuggestions';
import { educationMonthProblem } from './wholeAnswer';

/**
 * The Person page's Education, Other Skills and Profile tiles (piece 13;
 * decision 6180; T2872). Each reads its own route under the Person, and each
 * row a person accepted onto the page is one a person can take off it
 * (decision 5462).
 */

/** 🔒 The kinds of `PersonProfileFact` (piece 10). */
export const PERSON_PROFILE_FACT_KINDS = ['LinkedInPage', 'ListedSkill', 'Seniority', 'JobRole', 'JobSubRole', 'YearsExperience', 'SalaryBand', 'EmployerIndustry'] as const;
export type PersonProfileFactKind = (typeof PERSON_PROFILE_FACT_KINDS)[number];

/** 🔒 The six kinds the vendor assumes, labelled Assumed wherever they read. */
export const ASSUMED_FACT_KINDS: readonly PersonProfileFactKind[] = ['Seniority', 'JobRole', 'JobSubRole', 'YearsExperience', 'SalaryBand', 'EmployerIndustry'];

/** Each kind's label on the Profile tile. */
export const PROFILE_FACT_LABELS: Readonly<Record<PersonProfileFactKind, string>> = {
  LinkedInPage: 'LinkedIn Page',
  ListedSkill: 'Skill',
  Seniority: 'Seniority',
  JobRole: 'Role',
  JobSubRole: 'Sub-Role',
  YearsExperience: 'Years of Experience',
  SalaryBand: 'Salary Band',
  EmployerIndustry: 'Employer Industry',
};

/** 🔒 The fact kind a suggestion of the person's profile writes on Accept; a skill outside the catalog writes a listed skill. */
export const PROFILE_FACT_OF_FIELD: Readonly<Partial<Record<RecordCheckField, PersonProfileFactKind>>> = {
  LinkedInPage: 'LinkedInPage',
  Skill: 'ListedSkill',
  Seniority: 'Seniority',
  JobRole: 'JobRole',
  JobSubRole: 'JobSubRole',
  YearsExperience: 'YearsExperience',
  SalaryBand: 'SalaryBand',
  EmployerIndustry: 'EmployerIndustry',
};

/** 🔒 The routes: a person's schools, one school, a person's profile facts, one fact. */
export function personEducationPath(personId: string): string {
  return `${PEOPLE_PATH}/${encodeURIComponent(personId)}/education`;
}
export function personEducationRowPath(personId: string, educationId: string): string {
  return `${personEducationPath(personId)}/${encodeURIComponent(educationId)}`;
}
export function personProfilePath(personId: string): string {
  return `${PEOPLE_PATH}/${encodeURIComponent(personId)}/profile`;
}
export function personProfileFactPath(personId: string, factId: string): string {
  return `${personProfilePath(personId)}/${encodeURIComponent(factId)}`;
}

/** 🔒 One school as the Education tile draws it and its window edits it. */
export interface PersonEducationRow {
  id: string;
  version: number;
  school: string;
  degree: string | null;
  fieldOfStudy: string | null;
  startMonth: string | null;
  endMonth: string | null;
  schoolLinkedInUrl: string | null;
}

/** What a person types in the Education window. */
export interface PersonEducationDraft {
  school: string;
  degree?: string | null;
  fieldOfStudy?: string | null;
  startMonth?: string | null;
  endMonth?: string | null;
  schoolLinkedInUrl?: string | null;
}

/** The Education tile's read: the rows, newest first, and whether this viewer adds, corrects and removes them. */
export interface PersonEducationView {
  rows: PersonEducationRow[];
  writes: boolean;
}

/** One fact on the Profile or the Other Skills tile. */
export interface PersonProfileFactRow {
  id: string;
  kind: PersonProfileFactKind;
  value: string;
  assumed: boolean;
}

/** The Profile and Other Skills tiles' read. */
export interface PersonProfileView {
  facts: PersonProfileFactRow[];
  writes: boolean;
}

/** 🔒 The longest value a Profile row holds (decision 6661). */
export const PROFILE_FACT_VALUE_MAX = 300;

/** 🔒 A Profile row's correction: the value it now reads and the value the window read, which is its guard (decision 6661). */
export interface PersonProfileFactEdit {
  value: string;
  was: string;
}

/**
 * 🔨 The refusal a corrected Profile value earns, or null (decision 6661): a
 * value is written, since the remove is what clears a row; it is plain text of
 * 300 characters or fewer; and a LinkedIn page is an address on linkedin.com.
 */
export function profileFactProblem(kind: PersonProfileFactKind, value: string): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (text === '') return 'A value is written; Remove clears a row.';
  if (text.length > PROFILE_FACT_VALUE_MAX || /[<>]/.test(text)) return 'A value is plain text of 300 characters or fewer.';
  if (kind === 'LinkedInPage' && linkedInPage(text) === null) return 'A LinkedIn page is an address on linkedin.com.';
  return null;
}

/** The longest school, degree or field a row holds. */
export const EDUCATION_TEXT_MAX = 300;

/** 🔨 The refusal a draft earns, or null: a school is named, each text is plain and of a sane length, and each month is a year or a year and month. */
export function educationDraftProblem(draft: PersonEducationDraft): string | null {
  if (typeof draft.school !== 'string' || draft.school.trim() === '') return 'A school is named.';
  for (const text of [draft.school, draft.degree, draft.fieldOfStudy, draft.schoolLinkedInUrl]) {
    if (typeof text === 'string' && (text.length > EDUCATION_TEXT_MAX || /[<>]/.test(text))) return 'A school, degree or field is plain text of 300 characters or fewer.';
  }
  const start = educationMonthProblem(draft.startMonth);
  if (start) return start;
  const end = educationMonthProblem(draft.endMonth);
  if (end) return end;
  if (draft.startMonth && draft.endMonth) {
    const [start2, end2] = [draft.startMonth.trim(), draft.endMonth.trim()];
    const shared = Math.min(start2.length, end2.length);
    if (end2.slice(0, shared) < start2.slice(0, shared)) return 'The end falls before the start.';
  }
  if (draft.schoolLinkedInUrl && draft.schoolLinkedInUrl.trim() !== '' && linkedInPage(draft.schoolLinkedInUrl) === null) return 'A LinkedIn page is an address on linkedin.com.';
  return null;
}
